import React, { useState, useRef, useEffect } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';

const COOLDOWN_SECONDS = 300; // 5 minutos

const RecoverPassword = ({ onBack }) => {
  const [recoverEmail, setRecoverEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [successAnim, setSuccessAnim] = useState(false);
  const [recoverMessage, setRecoverMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownInterval = useRef();
  const successTimeout = useRef();

  useEffect(() => {
    if (recoverMessage && typeof recoverMessage !== 'string') {
      successTimeout.current = setTimeout(() => setShowSuccess(true), 50);
    } else {
      setShowSuccess(false);
    }
    return () => clearTimeout(successTimeout.current);
  }, [recoverMessage]);

  useEffect(() => {
    if (cooldown > 0) {
      cooldownInterval.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(cooldownInterval.current);
  }, [cooldown]);

  const handleRecoverSubmit = async (e) => {
    e.preventDefault();
    if (cooldown > 0) return;
    setRecoverMessage('');
    setLoading(true);
    setSuccessAnim(false);
    const startTime = Date.now();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTHAPI}/send-reset-password-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: recoverEmail })
        }
      );
      const data = await response.json();
      if (response.ok) {
        if (data?.status === 'success' || data?.result?.status === 'success') {
          setRecoverMessage(
            <span>
              <strong>E-mail de recuperação enviado com sucesso!</strong> Cheque sua caixa de entrada.
            </span>
          );
          setCooldown(COOLDOWN_SECONDS);
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(500 - elapsedTime, 0);
          setTimeout(() => {
            setSuccessAnim(true);
            setLoading(false);
            setTimeout(() => {
              setSuccessAnim(false);
            }, 1200);
          }, remainingTime);
        } else if (data?.not_found) {
          setRecoverMessage('E-mail não cadastrado na plataforma.');
          setLoading(false);
        } else {
          setRecoverMessage(
            'Erro ao enviar e-mail. Recarregue a página e tente novamente. Desculpe pelo transtorno!'
          );
          setLoading(false);
        }
      } else {
        if (data?.not_found) {
          setRecoverMessage('E-mail não cadastrado na plataforma.');
        } else {
          setRecoverMessage(
            'Erro ao enviar e-mail. Recarregue a página e tente novamente. Desculpe pelo transtorno!'
          );
        }
        setLoading(false);
      }
    } catch (err) {
      setRecoverMessage(
        'Erro ao enviar e-mail. Recarregue a página e tente novamente. Desculpe pelo transtorno!'
      );
      setLoading(false);
    }
  };

  // Barra de progresso do cooldown (0 a 100)
  const cooldownPercent = cooldown > 0 ? ((COOLDOWN_SECONDS - cooldown) / COOLDOWN_SECONDS) * 100 : 100;
  const cooldownMinutes = Math.floor(cooldown / 60);
  const cooldownSeconds = cooldown % 60;

  return (
    <form onSubmit={handleRecoverSubmit} style={{ marginBottom: '80px' }}>
      <div className="back-arrow">
        <ArrowBackIcon onClick={onBack} style={{ cursor: 'pointer' }} />
      </div>
      <h2>Recuperação de Senha</h2>
      <div className="input-group">
        <input
          type="email"
          placeholder="Digite seu e-mail"
          value={recoverEmail}
          onChange={(e) => setRecoverEmail(e.target.value)}
          required
          disabled={successAnim || cooldown > 0}
          style={successAnim || cooldown > 0 ? { background: '#f1f1f1', color: '#888', cursor: 'not-allowed' } : {}}
        />
      </div>
      <div className="recuparar-senha" style={{ display: 'flex', justifyContent: 'center', position: 'relative', width: '100%' }}>
        <button
          type="submit"
          className={`sign-in ${loading ? 'loading' : ''} ${successAnim ? 'success' : ''} ${cooldown > 0 ? 'cooldown' : ''}`}
          disabled={loading || successAnim || cooldown > 0}
          style={{
            position: 'relative',
            minWidth: 140,
            overflow: 'hidden',
            color: cooldown > 0 ? '#888' : undefined,
            background: cooldown > 0 ? '#fff' : undefined
          }}
        >
          {/* Barra de progresso do cooldown */}
          {cooldown > 0 && (
            <span
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${cooldownPercent}%`,
                background: 'linear-gradient(90deg, #007bff, #0056b3)',
                zIndex: 0,
                transition: 'width 1s linear'
              }}
            />
          )}
          <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {loading && (
              <span className="spinner" style={{
                width: 20, height: 20, border: '3px solid #b7e4c7', borderTop: '3px solid #218838',
                borderRadius: '50%', display: 'inline-block', marginRight: 8, verticalAlign: 'middle',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {successAnim && (
              <CheckIcon style={{ color: "#fff", marginRight: 8, verticalAlign: 'middle' }} />
            )}
            {successAnim
              ? 'Enviado!'
              : loading
                ? 'Enviando...'
                : cooldown > 0
                  ? `Aguarde ${cooldownMinutes}:${cooldownSeconds.toString().padStart(2, '0')}`
                  : 'Enviar'}
          </span>
        </button>
      </div>
      {/* Mensagem de sucesso/erro com transição suave */}
      {recoverMessage && (
        <div className={
          typeof recoverMessage === 'string'
            ? (recoverMessage.startsWith('E-mail não cadastrado') ? 'error-message' : 'error-message')
            : `success-message animated-success-message${showSuccess ? ' show' : ''}`
        }>
          {recoverMessage}
        </div>
      )}
    </form>
  );
};

export default RecoverPassword;
